import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnDestroy,
	output,
	signal,
	untracked,
	ViewEncapsulation,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { primaryServiceSlugForSchemaField } from '@jepto/shared/utils/data-source-icon-meta.util';
import type { ThemeConfig } from '../../../models/theme.model';
import type { DimensionFilterCardData } from '../../../models/page-filter.model';
import { ThemeService, type ThemeMode } from '../../../services/theme.service';
import { inferFilterCardDataSourceFromMember } from '../../../utils/filter-data-source.util';
import { buildDimensionFilterSummaryLabel } from '../../../utils/dimension-filter-summary.util';
import {
	resolveFilterControlIsDark,
	resolveFilterControlThemeColors,
} from '../../../utils/filter-control-theme.util';
import {
	resolveAnalyticsServiceIconAsset,
	resolveAnalyticsServiceMaterialIcon,
	type AnalyticsServiceIconAsset,
} from '../../../utils/analytics-service-icon-assets';

/** Delay before notifying parents after the last checkbox change in the menu. */
const SELECTION_CHANGE_DEBOUNCE_MS = 1000;

@Component({
	selector: 'jepto-dimension-filter-dropdown',
	imports: [CommonModule, MatCheckboxModule, MatIconModule, MatMenuModule],
	templateUrl: './jepto-dimension-filter-dropdown.component.html',
	styleUrl: './jepto-dimension-filter-dropdown.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	host: {
		class: 'jepto-dimension-filter-dropdown',
		'[class.jepto-dimension-filter-dropdown--dark]': 'isDark()',
		'[class.jepto-dimension-filter-dropdown--empty]': 'isEmpty()',
		'[class.jepto-dimension-filter-dropdown--menu-disabled]': 'menuDisabled()',
		'[style.--dimension-filter-bg]': 'themeColors().background',
		// Figma 39500:94398 — trigger outline is border/secondary (#e2e3e5 light).
		'[style.--dimension-filter-border]': 'themeColors().borderSecondary',
		'[style.--dimension-filter-text-primary]': 'themeColors().textPrimary',
		'[style.--dimension-filter-text-secondary]':
			'themeColors().textSecondary',
		'[style.--dimension-filter-hover-mix]': 'themeColors().hoverMix',
		'[style.--dimension-filter-accent]': 'themeColors().accent',
	},
})
export class JeptoDimensionFilterDropdownComponent implements OnDestroy {
	private readonly themeService = inject(ThemeService);

	#selectionEmitTimer: ReturnType<typeof setTimeout> | null = null;

	readonly testId = input<string | undefined>();
	readonly filterData = input.required<DimensionFilterCardData>();
	/** Follows card / layout theme; `auto` uses {@link ThemeService} active mode. */
	readonly themeMode = input<ThemeMode>('auto');
	readonly themeConfig = input<ThemeConfig | undefined>(undefined);
	/** When true, the menu cannot be opened and selections cannot change (e.g. report edit layout). */
	readonly menuDisabled = input<boolean>(false);
	readonly selectionChange = output<string[]>();

	readonly isDark = computed(() =>
		resolveFilterControlIsDark(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		)
	);

	readonly themeColors = computed(() =>
		resolveFilterControlThemeColors(
			this.isDark(),
			this.themeConfig() ?? this.themeService.getThemeConfig()
		)
	);

	readonly menuPanelClass = computed(() =>
		this.isDark()
			? 'jepto-dimension-filter-dropdown__panel jepto-dimension-filter-dropdown__panel--dark'
			: 'jepto-dimension-filter-dropdown__panel'
	);

	readonly dimensionLabel = computed(() => {
		const { label, member } = this.filterData().dimension;
		return label?.trim() || member?.trim() || '';
	});

	readonly isEmpty = computed(
		() => !this.filterData().dimension.member?.trim()
	);

	/**
	 * Blocks opening the menu (empty config or report edit / drag mode).
	 * Does not use the native `disabled` attribute for `menuDisabled` — that
	 * washes out border/chevron under app UA styles while the preview stays enabled.
	 */
	readonly isMenuBlocked = computed(
		() => this.isEmpty() || this.menuDisabled()
	);

	/** Native disabled only when there is nothing to select yet. */
	readonly isTriggerNativeDisabled = computed(() => this.isEmpty());

	/**
	 * Connector slug for the trigger icon. Runtime filter payloads only carry
	 * `dimension.member` — infer `dataSource` from the cube prefix so icons keep
	 * working after `primaryServiceSlugForSchemaField` became dataSource-only.
	 */
	readonly dimensionServiceSlug = computed((): string | undefined => {
		const member = this.filterData().dimension.member?.trim();
		if (!member) {
			return undefined;
		}
		return primaryServiceSlugForSchemaField({
			name: member,
			dataSource: inferFilterCardDataSourceFromMember(member),
		});
	});

	iconAssetForService(
		slug: string | undefined
	): AnalyticsServiceIconAsset | undefined {
		return resolveAnalyticsServiceIconAsset(slug);
	}

	materialIconForService(slug: string | undefined): string | undefined {
		return resolveAnalyticsServiceMaterialIcon(slug);
	}

	private readonly _searchQuery = signal('');
	protected readonly searchQuery = this._searchQuery.asReadonly();
	private readonly _selected = signal<string[]>([]);

	readonly summaryLabel = computed(() =>
		buildDimensionFilterSummaryLabel(
			this.filterData().options,
			this._selected()
		)
	);

	readonly filteredOptions = computed(() => {
		const q = this._searchQuery().trim().toLowerCase();
		const options = this.filterData().options;
		if (!q) {
			return options;
		}
		return options.filter(
			o =>
				o.label.toLowerCase().includes(q) ||
				o.value.toLowerCase().includes(q)
		);
	});

	readonly allFilteredSelected = computed(() => {
		const filtered = this.filteredOptions();
		if (filtered.length === 0) {
			return false;
		}
		const selected = new Set(this._selected());
		return filtered.every(o => selected.has(o.value));
	});

	readonly someFilteredSelected = computed(() => {
		const filtered = this.filteredOptions();
		const selected = this._selected();
		const count = filtered.filter(o => selected.includes(o.value)).length;
		return count > 0 && count < filtered.length;
	});

	constructor() {
		effect(() => {
			const data = this.filterData();
			untracked(() => {
				// Keep in-progress checkbox edits. Parent/WS option refreshes often
				// re-push default selected=all during the 1s debounce; cancelling
				// here re-checked the option the user just cleared.
				if (this.#selectionEmitTimer !== null) {
					return;
				}
				this._selected.set([...data.selected]);
				this._searchQuery.set('');
			});
		});
	}

	ngOnDestroy(): void {
		this.#cancelPendingSelectionEmit();
	}

	isOptionSelected(value: string): boolean {
		return this._selected().includes(value);
	}

	onSearchInput(value: string): void {
		if (this.menuDisabled()) {
			return;
		}
		this._searchQuery.set(value);
	}

	onToggleOption(value: string, checked: boolean): void {
		if (this.menuDisabled()) {
			return;
		}
		const next = new Set(this._selected());
		if (checked) {
			next.add(value);
		} else {
			next.delete(value);
		}
		const values = [...next];
		this._selected.set(values);
		this.#scheduleSelectionChangeEmit();
	}

	onSelectAllFiltered(checked: boolean): void {
		if (this.menuDisabled()) {
			return;
		}
		const filtered = this.filteredOptions();
		const next = new Set(this._selected());
		for (const opt of filtered) {
			if (checked) {
				next.add(opt.value);
			} else {
				next.delete(opt.value);
			}
		}
		const values = [...next];
		this._selected.set(values);
		this.#scheduleSelectionChangeEmit();
	}

	onMenuClosed(): void {
		this.#flushPendingSelectionChangeEmit();
		this._searchQuery.set('');
	}

	#scheduleSelectionChangeEmit(): void {
		if (this.#selectionEmitTimer !== null) {
			clearTimeout(this.#selectionEmitTimer);
		}
		this.#selectionEmitTimer = setTimeout(() => {
			this.#selectionEmitTimer = null;
			this.selectionChange.emit([...this._selected()]);
		}, SELECTION_CHANGE_DEBOUNCE_MS);
	}

	#flushPendingSelectionChangeEmit(): void {
		if (this.#selectionEmitTimer === null) {
			return;
		}
		clearTimeout(this.#selectionEmitTimer);
		this.#selectionEmitTimer = null;
		this.selectionChange.emit([...this._selected()]);
	}

	#cancelPendingSelectionEmit(): void {
		if (this.#selectionEmitTimer === null) {
			return;
		}
		clearTimeout(this.#selectionEmitTimer);
		this.#selectionEmitTimer = null;
	}
}
